class AtdVideoScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.dotsContainer = this.querySelector('[data-dots]');
    this.cards = Array.from(this.querySelectorAll('.atd-video-card'));
    this.videos = Array.from(this.querySelectorAll('[data-atd-video]'));

    this.buildDots();
    this.bindEvents();
    this.warmVisible();
    this.updateActiveDot();
    this.updateControls();

    // Recalcular si cambia el ancho (rotación, resize, theme editor).
    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
  }

  // Sube a preload="metadata" SOLO las tarjetas que están a la vista: unos KB
  // por video (el moov va al inicio) y con eso el navegador ya tiene el primer
  // cuadro y las dimensiones listas para cuando alguien toque play. Las que
  // están fuera del carrusel visible siguen en "none" y no piden nada, que era
  // el problema de precargarlas todas.
  warmVisible() {
    if (!('IntersectionObserver' in window)) return;

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const video = entry.target.querySelector('[data-atd-video]');
          if (!video || video.preload !== 'none') return;
          // Solo metadata: NO se llama load(), que dispararía la descarga.
          video.preload = 'metadata';
        });
      },
      { root: this.track, threshold: 0.6 }
    );

    this.cards.forEach((card) => this.visibilityObserver.observe(card));
  }

  // Oculta flechas + puntos cuando los videos caben completos (no hay scroll
  // que recorrer). Funciona en escritorio y móvil porque mide el desbordamiento
  // real del track, no el número de tarjetas configurado.
  updateControls() {
    if (!this.track) return;
    const isScrollable = this.track.scrollWidth - this.track.clientWidth > 2;
    this.classList.toggle('atd-video-scroller1--static', !isScrollable);

    // El número de páginas cambia con el ancho (en móvil se ve 1 tarjeta, en
    // escritorio 5), así que los puntos se reconstruyen cuando cambia.
    const pages = this.getMetrics().pages;
    if (pages !== this._pages) {
      this.buildDots();
      this.updateActiveDot();
    }
  }

  /**
   * Un punto por POSICIÓN DE SCROLL alcanzable, no por tarjeta.
   *
   * Antes había un punto por video: con 7 videos y 5 visibles salían 7 puntos
   * pero solo 3 posiciones reales, así que los últimos 4 no se activaban nunca
   * y el avance de las flechas no cuadraba con los puntos (reportado en
   * Macaria, 2026-08-14). Las flechas avanzan de una tarjeta, así que las
   * posiciones son 0, step, 2*step... hasta el final del recorrido.
   */
  getMetrics() {
    const card = this.track?.querySelector('.atd-video-card');
    if (!this.track || !card) return { step: 0, maxScroll: 0, pages: 1 };

    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const step = card.offsetWidth + gap;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;

    if (step <= 0 || maxScroll <= 2) return { step, maxScroll: 0, pages: 1 };

    // +1 porque la posición inicial (scroll 0) también es una página.
    // round y no ceil: con ceil, un sobrante de pocos píxeles creaba un punto
    // extra a 10px del anterior, indistinguible al ojo.
    const pages = Math.round(maxScroll / step) + 1;
    return { step, maxScroll, pages };
  }

  buildDots() {
    if (!this.dotsContainer) return;
    const { pages } = this.getMetrics();
    this._pages = pages;
    this.dotsContainer.innerHTML = '';

    // Con una sola página no hay nada que recorrer: sin puntos.
    if (pages <= 1) {
      this.dots = [];
      return;
    }

    for (let i = 0; i < pages; i += 1) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'atd-video-scroller1__dot';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', `Ir a la posición ${i + 1} de ${pages}`);
      btn.addEventListener('click', () => this.goToPage(i));
      this.dotsContainer.appendChild(btn);
    }
    this.dots = Array.from(this.dotsContainer.children);
  }

  goToPage(index) {
    const { step, maxScroll, pages } = this.getMetrics();
    if (!this.track || step <= 0) return;
    // El último punto siempre lleva al final del recorrido, aunque el paso
    // calculado se quede corto: si no, quedaría un trozo inalcanzable.
    const left = index >= pages - 1 ? maxScroll : Math.min(index * step, maxScroll);
    this.track.scrollTo({ left, behavior: 'smooth' });
  }

  updateActiveDot() {
    if (!this.dots || !this.dots.length || !this.track) return;
    const { step, maxScroll } = this.getMetrics();
    if (step <= 0) return;

    const scrollLeft = this.track.scrollLeft;
    // El último punto se marca al llegar al tope: la última página suele ser
    // más corta que un paso completo, así que redondear no basta.
    const activeIndex =
      scrollLeft >= maxScroll - 2
        ? this.dots.length - 1
        : Math.min(Math.round(scrollLeft / step), this.dots.length - 1);

    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.scrollByCard(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByCard(1));

    this.querySelectorAll('[data-atd-play]').forEach((btn, i) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.play(i);
      });
    });

    // Click sobre el video pausa
    this.videos.forEach((video, i) => {
      const card = video.closest('.atd-video-card');
      video.addEventListener('click', () => {
        if (!video.paused) {
          video.pause();
        } else {
          this.play(i);
        }
      });
      video.addEventListener('pause', () => card?.classList.remove('is-playing'));
      video.addEventListener('ended', () => card?.classList.remove('is-playing'));
    });

    this.track?.addEventListener('scroll', () => this.updateActiveDot(), { passive: true });
  }

  scrollByCard(direction) {
    if (!this.track) return;
    const card = this.track.querySelector('.atd-video-card');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).gap || '0');
    const step = card.offsetWidth + gap;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;

    // Loop infinito: si estás en el extremo, salta al opuesto
    if (direction > 0 && scrollLeft >= maxScroll - 2) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (direction < 0 && scrollLeft <= 2) {
      this.track.scrollTo({ left: maxScroll, behavior: 'smooth' });
    } else {
      this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
    }
  }

  play(index) {
    this.videos.forEach((video, i) => {
      const card = video.closest('.atd-video-card');
      if (i !== index) {
        video.pause();
        card?.classList.remove('is-playing');
        card?.classList.remove('is-loading');
        // Pausar no basta: un <video> pausado con preload="auto" sigue
        // descargando y ocupando el pipeline de media. Con varios abiertos,
        // uno de los siguientes se queda ~15 s sin arrancar (medido, ago-2026).
        // Devolverlo a preload="none" + load() lo suelta y repinta su poster;
        // los <source> siguen ahí para cuando se le vuelva a dar play.
        if (video.preload !== 'none') {
          video.preload = 'none';
          video.load();
        }
      }
    });
    const video = this.videos[index];
    if (!video) return;
    video.closest('.atd-video-card')?.classList.add('is-playing');
    // Los <video> nacen con preload="none" (ver la sección): nadie descarga
    // nada hasta este clic. load() aquí, dentro del handler del gesto, sigue
    // contando como interacción del usuario, así que el play() de abajo no se
    // bloquea.
    if (video.preload === 'none') {
      video.preload = 'auto';
      video.load();
    }

    // Estado "cargando": el <video> aún no tiene cuadro que pintar, así que se
    // oculta (el fondo del contenedor sigue mostrando la miniatura) y aparece
    // el spinner. Sin esto el visitante ve un rectángulo negro y no sabe si
    // pasó algo — reportado desde un iPhone en 5G (2026-08-15).
    const card = video.closest('.atd-video-card');
    if (video.readyState < 3) {
      card?.classList.add('is-loading');
      const listo = () => {
        card?.classList.remove('is-loading');
        video.removeEventListener('playing', listo);
        video.removeEventListener('error', listo);
      };
      video.addEventListener('playing', listo);
      video.addEventListener('error', listo);
    }

    video.play().catch(() => {
      video.muted = true;
      video.play();
    });
  }
}

if (!customElements.get('atd-video-scroller')) {
  customElements.define('atd-video-scroller', AtdVideoScroller);
}
